/**
 * Production error monitoring (browser side).
 *
 * Captures, de-duplicates and batches:
 *  - uncaught errors and unhandled promise rejections
 *  - React render crashes / route errors (reportError)
 *  - failed and aborted fetch requests
 *  - blank-screen watchdog (hydrated but nothing painted)
 *
 * Events are POSTed to /api/public/client-log, which stores them server-side.
 * Stack traces stay readable in production because the build emits source maps.
 */

export type ErrorKind =
  | "js_error"
  | "unhandled_rejection"
  | "react_error"
  | "route_error"
  | "network_error"
  | "aborted_request"
  | "blank_screen"
  | "console_error";

type ErrorEvent = {
  kind: ErrorKind;
  message: string;
  stack?: string;
  fingerprint: string;
  pageUrl?: string;
  route?: string;
  source?: string;
  line?: number;
  col?: number;
  sessionId: string;
  userId?: string;
  release?: string;
  occurrences: number;
  extra?: Record<string, unknown>;
};

const ENDPOINT = "/api/public/client-log";
const FLUSH_DELAY = 4000;
const MAX_QUEUE = 20;
const MAX_PER_SESSION = 60;
const SESSION_KEY = "xamseen_error_session";

const RELEASE = import.meta.env.VITE_APP_RELEASE ?? import.meta.env.MODE;

/** Noise that says nothing about app health. */
const IGNORED = [
  /ResizeObserver loop/i,
  /Script error\.?$/i,
  /Load failed$/i,
  /^Network request failed$/i,
  /extension context invalidated/i,
  /Non-Error promise rejection captured with value: undefined/i,
];

let queue: ErrorEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let sentThisSession = 0;
let installed = false;
let currentUserId: string | undefined;

function sessionId(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const id = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, id);
    return id;
  } catch {
    return "no-session-storage";
  }
}

function fingerprintOf(kind: string, message: string, stack?: string): string {
  const frame = stack?.split("\n").find((l) => /https?:\/\/|\.tsx?|\.js/.test(l))?.trim() ?? "";
  const normalized = `${kind}|${message.replace(/\d+/g, "#").slice(0, 160)}|${frame
    .replace(/:\d+:\d+/g, "")
    .slice(0, 120)}`;
  let hash = 0;
  for (let i = 0; i < normalized.length; i++) {
    hash = (hash * 31 + normalized.charCodeAt(i)) | 0;
  }
  return `${kind}-${Math.abs(hash).toString(36)}`;
}

function flush(useBeacon = false) {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (queue.length === 0) return;

  const body = JSON.stringify({ events: queue.slice(0, MAX_QUEUE) });
  queue = [];

  try {
    if (useBeacon && typeof navigator !== "undefined" && navigator.sendBeacon) {
      navigator.sendBeacon(ENDPOINT, new Blob([body], { type: "application/json" }));
      return;
    }
    void fetch(ENDPOINT, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {
      /* reporting must never surface an error of its own */
    });
  } catch {
    /* ignore */
  }
}

function schedule() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => flush(), FLUSH_DELAY);
}

/** Records one event. Safe to call from anywhere; never throws. */
export function reportError(
  kind: ErrorKind,
  error: unknown,
  extra?: Record<string, unknown>,
): void {
  try {
    if (typeof window === "undefined") return;
    if (sentThisSession >= MAX_PER_SESSION) return;

    const err = error instanceof Error ? error : undefined;
    const message = (err?.message ?? String(error ?? "Unknown error")).slice(0, 1000);
    if (!message || IGNORED.some((re) => re.test(message))) return;

    const stack = err?.stack?.slice(0, 8000);
    const fingerprint = fingerprintOf(kind, message, stack);

    const existing = queue.find((e) => e.fingerprint === fingerprint);
    if (existing) {
      existing.occurrences = Math.min(existing.occurrences + 1, 1000);
      return;
    }

    sentThisSession += 1;
    queue.push({
      kind,
      message,
      ...(stack ? { stack } : {}),
      fingerprint,
      pageUrl: window.location.href.slice(0, 600),
      route: window.location.pathname.slice(0, 300),
      sessionId: sessionId(),
      ...(currentUserId ? { userId: currentUserId } : {}),
      release: String(RELEASE).slice(0, 80),
      occurrences: 1,
      ...(extra ? { extra } : {}),
    });

    if (queue.length >= MAX_QUEUE) flush();
    else schedule();
  } catch {
    /* ignore */
  }
}

/** Attaches the signed-in user id to subsequent reports. */
export function setErrorUser(userId: string | null | undefined): void {
  currentUserId = userId ?? undefined;
}

function wrapFetch() {
  const original = window.fetch;
  window.fetch = async function patchedFetch(input, init) {
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    try {
      const response = await original(input as RequestInfo, init);
      if (response.status >= 500 && !url.includes(ENDPOINT)) {
        reportError("network_error", new Error(`HTTP ${response.status} on ${safeUrl(url)}`), {
          status: response.status,
          url: safeUrl(url),
        });
      }
      return response;
    } catch (err) {
      if (!url.includes(ENDPOINT)) {
        const aborted =
          (err as { name?: string })?.name === "AbortError" ||
          /aborted/i.test((err as Error)?.message ?? "");
        reportError(
          aborted ? "aborted_request" : "network_error",
          new Error(`${aborted ? "Aborted" : "Failed"} request: ${safeUrl(url)}`),
          { url: safeUrl(url), online: navigator.onLine },
        );
      }
      throw err;
    }
  };
}

/** Strips query strings so tokens in URLs are never stored. */
function safeUrl(url: string): string {
  try {
    const u = new URL(url, window.location.origin);
    return `${u.origin}${u.pathname}`.slice(0, 300);
  } catch {
    return url.split("?")[0]!.slice(0, 300);
  }
}

/** Catches the "hydrated but nothing rendered" blank screen. */
function blankScreenWatchdog() {
  const check = () => {
    const root = document.body;
    const text = (root?.innerText ?? "").trim();
    const painted = root?.querySelectorAll("main, header, nav, article, form, [data-app-shell]").length ?? 0;
    if (text.length < 8 && painted === 0) {
      reportError("blank_screen", new Error("Blank screen after load"), {
        readyState: document.readyState,
        childNodes: root?.childElementCount ?? 0,
      });
      flush();
    }
  };
  setTimeout(check, 8000);
}

/** Installs all listeners once per page load. */
export function initErrorMonitoring(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("error", (event) => {
    if (event.error) {
      reportError("js_error", event.error, {
        source: event.filename,
        line: event.lineno,
        col: event.colno,
      });
      return;
    }
    // Resource load failure (script/style/image) — event.target has no error object.
    const target = event.target as HTMLElement | null;
    const src = (target as HTMLImageElement | HTMLScriptElement | null)?.src;
    if (src) {
      reportError("network_error", new Error(`Failed to load ${safeUrl(src)}`), {
        tag: target?.tagName?.toLowerCase(),
      });
    }
  }, true);

  window.addEventListener("unhandledrejection", (event) => {
    reportError("unhandled_rejection", event.reason);
  });

  window.addEventListener("pagehide", () => flush(true));
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush(true);
  });

  wrapFetch();
  blankScreenWatchdog();
}
